import { cn } from '@/utils';
import { formatDateTime } from '@/utils';
import { formatDistanceToNow } from 'date-fns';

export interface ActivityItem {
  id: string;
  type: 'create' | 'update' | 'delete' | 'login' | 'logout' | 'info' | 'warning' | 'error' | 'success';
  title: string;
  description?: string;
  timestamp: string | Date;
  user?: {
    name: string;
    avatar?: string;
  };
  metadata?: Record<string, string | number>;
}

interface ActivityTimelineProps {
  activities: ActivityItem[];
  loading?: boolean;
  className?: string;
  maxItems?: number;
  showTimestamp?: boolean;
}

const typeStyles = {
  create: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    dot: 'bg-green-500',
  },
  update: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-500',
  },
  delete: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
    dot: 'bg-red-500',
  },
  login: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    dot: 'bg-purple-500',
  },
  logout: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-600 dark:text-gray-400',
    dot: 'bg-gray-500',
  },
  info: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-500',
  },
  warning: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-600 dark:text-yellow-400',
    dot: 'bg-yellow-500',
  },
  error: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
    dot: 'bg-red-500',
  },
  success: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    dot: 'bg-green-500',
  },
};

export function ActivityTimeline({
  activities,
  loading,
  className,
  maxItems,
  showTimestamp = true,
}: ActivityTimelineProps) {
  const displayActivities = maxItems ? activities.slice(0, maxItems) : activities;

  if (loading) {
    return (
      <div className={cn('space-y-4', className)}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 animate-pulse">
            <div className="w-2 h-2 mt-2 rounded-full bg-gray-300 dark:bg-gray-600" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (displayActivities.length === 0) {
    return (
      <div className={cn('text-center py-8 text-gray-500 dark:text-gray-400', className)}>
        No recent activity
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {/* Vertical line */}
      <div className="absolute left-1 top-2 bottom-2 w-0.5 bg-gray-200 dark:bg-gray-700" />

      <div className="space-y-6">
        {displayActivities.map((activity) => {
          const styles = typeStyles[activity.type];
          const timestamp = new Date(activity.timestamp);

          return (
            <div key={activity.id} className="relative flex gap-4 pl-6">
              {/* Dot indicator */}
              <div
                className={cn(
                  'absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-white dark:ring-gray-900',
                  styles.dot
                )}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {activity.title}
                    </p>
                    {activity.description && (
                      <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {activity.description}
                      </p>
                    )}
                    {activity.user && (
                      <div className="mt-1 flex items-center gap-2">
                        {activity.user.avatar ? (
                          <img
                            src={activity.user.avatar}
                            alt={activity.user.name}
                            className="w-5 h-5 rounded-full"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                              {activity.user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {activity.user.name}
                        </span>
                      </div>
                    )}
                  </div>
                  {showTimestamp && (
                    <time
                      dateTime={timestamp.toISOString()}
                      className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500"
                      title={formatDateTime(timestamp)}
                    >
                      {formatDistanceToNow(timestamp, { addSuffix: true })}
                    </time>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ActivityCardProps {
  title?: string;
  activities: ActivityItem[];
  loading?: boolean;
  maxItems?: number;
  viewAllHref?: string;
  className?: string;
}

export function ActivityCard({
  title = 'Recent Activity',
  activities,
  loading,
  maxItems = 5,
  viewAllHref,
  className,
}: ActivityCardProps) {
  return (
    <div
      className={cn(
        'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6',
        className
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        {viewAllHref && (
          <a
            href={viewAllHref}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            View all
          </a>
        )}
      </div>
      <ActivityTimeline activities={activities} loading={loading} maxItems={maxItems} />
    </div>
  );
}
